import { useEffect, useRef, useState } from "react";
import { X, MapPin, Loader2, Send, Navigation } from "lucide-react";
import { enviarLocalizacao } from "../../services/mensagensApi";

/**
 * Modal para enviar uma localização via WhatsApp.
 * Permite usar geolocalização do browser ou inserir lat/lng manualmente.
 * Campos opcionais: nome e endereço (máx 1000 chars cada).
 *
 * @param {{
 *   conversaId: number,
 *   onClose: () => void,
 *   onSent: (mensagem: object) => void,
 * }} props
 */
export default function LocationSender({ conversaId, onClose, onSent }) {
  const [latitude,   setLatitude]   = useState("");
  const [longitude,  setLongitude]  = useState("");
  const [nome,       setNome]       = useState("");
  const [endereco,   setEndereco]   = useState("");
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoErro,    setGeoErro]    = useState(null);
  const [enviando,   setEnviando]   = useState(false);
  const [erroEnvio,  setErroEnvio]  = useState(null);
  const modalRef = useRef(null);

  useEffect(() => { modalRef.current?.focus(); }, []);

  useEffect(() => {
    function handleEsc(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  function usarGeolocalizacao() {
    if (!navigator.geolocation) {
      setGeoErro("Geolocalização não suportada neste navegador.");
      return;
    }
    setGeoLoading(true);
    setGeoErro(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLatitude(String(pos.coords.latitude));
        setLongitude(String(pos.coords.longitude));
        setGeoLoading(false);
      },
      (err) => {
        setGeoErro(
          err.code === 1
            ? "Permissão de localização negada."
            : "Não foi possível obter a localização."
        );
        setGeoLoading(false);
      },
      { timeout: 10000 }
    );
  }

  function validar() {
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (isNaN(lat) || lat < -90  || lat > 90)  return "Latitude inválida (entre -90 e 90).";
    if (isNaN(lng) || lng < -180 || lng > 180) return "Longitude inválida (entre -180 e 180).";
    if (nome     && nome.length     > 1000) return "Nome muito longo (máx 1000 caracteres).";
    if (endereco && endereco.length > 1000) return "Endereço muito longo (máx 1000 caracteres).";
    return null;
  }

  async function handleEnviar() {
    const validacaoErro = validar();
    if (validacaoErro) { setErroEnvio(validacaoErro); return; }

    setEnviando(true);
    setErroEnvio(null);
    try {
      const resultado = await enviarLocalizacao(conversaId, {
        latitude:  parseFloat(latitude),
        longitude: parseFloat(longitude),
        name:      nome.trim()     || null,
        address:   endereco.trim() || null,
      });
      onSent(resultado);
      onClose();
    } catch (err) {
      setErroEnvio(err?.response?.data?.error ?? "Erro ao enviar localização.");
    } finally {
      setEnviando(false);
    }
  }

  const podeEnviar = latitude.trim() !== "" && longitude.trim() !== "" && !enviando;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Enviar localização"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className="bg-surface-1 border border-white/[0.08] rounded-xl shadow-2xl w-full max-w-sm flex flex-col outline-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <h2 className="text-sm font-semibold text-ink-1 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-brand-400" aria-hidden="true" />
            Enviar localização
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar modal"
            className="text-ink-4 hover:text-ink-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded p-0.5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 flex flex-col gap-3">
          {/* Botão de geolocalização */}
          <button
            type="button"
            onClick={usarGeolocalizacao}
            disabled={geoLoading}
            aria-label="Usar minha localização atual"
            className="flex items-center justify-center gap-2 text-xs font-medium text-brand-300 border border-brand-700 hover:border-brand-500 hover:bg-brand-900 rounded-lg py-2.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
          >
            {geoLoading
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
              : <Navigation className="w-3.5 h-3.5" aria-hidden="true" />}
            {geoLoading ? "Obtendo localização..." : "Usar minha localização"}
          </button>

          {geoErro && (
            <p className="text-xs text-amber-400" role="alert">{geoErro}</p>
          )}

          <div className="flex items-center gap-2 text-ink-5">
            <div className="flex-1 h-px bg-surface-2" aria-hidden="true" />
            <span className="text-xs">ou insira manualmente</span>
            <div className="flex-1 h-px bg-surface-2" aria-hidden="true" />
          </div>

          {/* Coordenadas */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label htmlFor="loc-lat" className="text-xs text-ink-3">
                Latitude <span className="text-red-400" aria-label="obrigatório">*</span>
              </label>
              <input
                id="loc-lat"
                type="number"
                step="any"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                placeholder="-23.5505"
                aria-required="true"
                className="bg-surface-2 border border-white/[0.08] rounded px-2.5 py-1.5 text-xs text-ink-2 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="loc-lng" className="text-xs text-ink-3">
                Longitude <span className="text-red-400" aria-label="obrigatório">*</span>
              </label>
              <input
                id="loc-lng"
                type="number"
                step="any"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                placeholder="-46.6333"
                aria-required="true"
                className="bg-surface-2 border border-white/[0.08] rounded px-2.5 py-1.5 text-xs text-ink-2 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors"
              />
            </div>
          </div>

          {/* Nome (opcional) */}
          <div className="flex flex-col gap-1">
            <label htmlFor="loc-nome" className="text-xs text-ink-3">Nome (opcional)</label>
            <input
              id="loc-nome"
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              maxLength={1000}
              placeholder="Ex: Escritório Central"
              className="bg-surface-2 border border-white/[0.08] rounded px-2.5 py-1.5 text-xs text-ink-2 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors"
            />
          </div>

          {/* Endereço (opcional) */}
          <div className="flex flex-col gap-1">
            <label htmlFor="loc-end" className="text-xs text-ink-3">Endereço (opcional)</label>
            <input
              id="loc-end"
              type="text"
              value={endereco}
              onChange={(e) => setEndereco(e.target.value)}
              maxLength={1000}
              placeholder="Ex: Av. Paulista, 1000 — São Paulo"
              className="bg-surface-2 border border-white/[0.08] rounded px-2.5 py-1.5 text-xs text-ink-2 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors"
            />
          </div>

          {erroEnvio && (
            <p className="text-xs text-red-400" role="alert">{erroEnvio}</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-ink-3 hover:text-ink-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded px-3 py-1.5"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleEnviar}
            disabled={!podeEnviar}
            aria-label="Enviar localização"
            className="flex items-center gap-1.5 text-xs font-medium bg-brand-600 hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg px-4 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            {enviando
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
              : <Send className="w-3.5 h-3.5" aria-hidden="true" />}
            Enviar
          </button>
        </div>
      </div>
    </div>
  );
}
